import axios from "axios";
import authHeader from "./auth-header";
import ErrorService from "./error.service";
import UserService from "./user.service";
import AdminService from "./admin.service";

const BASE_URL = AdminService.getBaseUrl();
const API_CONTENT_URL = BASE_URL + "/api/v1/content";
const CONTENT_DB_NAME = "wratingsApp";
const CONTENT_DB_VERSION = 1;
const CONTENT_STORE_NAME = "pageContent";
const LEGACY_CONTENT_KEY = "content";

class ContentService {
  constructor() {
    this.dbPromise = null;
    this.migrationPromise = null;
  }

  getShareButtonTooltip(showOrHide) {
    return {
      placement: "right",
      content: "Click to copy the invitation link for respondents to complete this interview",
      trigger: "hover",
      delay: { show: 300, hide: 300 },
      classes: "share-button-tooltip-inner"
    };
  }

  getPreviewButtonTooltip(showOrHide) {
    return {
      placement: "right",
      content: "Click to see the respondent interview",
      trigger: "hover",
      delay: { show: 1000, hide: 3100 },
      classes: "preview-button-tooltip-inner"
    };
  }

  buildCacheKey(pageName) {
    return `content:${pageName}`;
  }

  isIndexedDbSupported() {
    return typeof window !== "undefined" && typeof window.indexedDB !== "undefined";
  }

  getLegacyContentList() {
    try {
      const storedContent = localStorage.getItem(LEGACY_CONTENT_KEY);
      return storedContent != null ? JSON.parse(storedContent) : [];
    } catch (error) {
      return [];
    }
  }

  setLegacyContentList(contentList) {
    try {
      localStorage.setItem(LEGACY_CONTENT_KEY, JSON.stringify(contentList));
    } catch (error) {
      // Safe fallback: ignore storage failures and continue without cache persistence.
    }
  }

  removeLegacyContentKey() {
    try {
      localStorage.removeItem(LEGACY_CONTENT_KEY);
    } catch (error) {
      // Ignore legacy cleanup failures.
    }
  }

  openDb() {
    if (!this.isIndexedDbSupported()) {
      return Promise.resolve(null);
    }
    if (this.dbPromise != null) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise(resolve => {
      try {
        const request = window.indexedDB.open(CONTENT_DB_NAME, CONTENT_DB_VERSION);

        request.onupgradeneeded = event => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains(CONTENT_STORE_NAME)) {
            db.createObjectStore(CONTENT_STORE_NAME, { keyPath: "cacheKey" });
          }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
      } catch (error) {
        resolve(null);
      }
    });

    return this.dbPromise;
  }

  runStoreOperation(mode, executor) {
    return this.openDb().then(db => {
      if (db == null) {
        return null;
      }

      return new Promise(resolve => {
        try {
          const transaction = db.transaction(CONTENT_STORE_NAME, mode);
          const store = transaction.objectStore(CONTENT_STORE_NAME);
          executor(store, resolve);
          transaction.onerror = () => resolve(null);
        } catch (error) {
          resolve(null);
        }
      });
    });
  }

  async getIndexedDbValue(cacheKey) {
    const result = await this.runStoreOperation("readonly", (store, resolve) => {
      const request = store.get(cacheKey);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => resolve(null);
    });
    return result;
  }

  async setIndexedDbValue(entry) {
    return this.runStoreOperation("readwrite", (store, resolve) => {
      const request = store.put(entry);
      request.onsuccess = () => resolve(true);
      request.onerror = () => resolve(false);
    });
  }

  async deleteIndexedDbValue(cacheKey) {
    return this.runStoreOperation("readwrite", (store, resolve) => {
      const request = store.delete(cacheKey);
      request.onsuccess = () => resolve(true);
      request.onerror = () => resolve(false);
    });
  }

  async getAllIndexedDbValues() {
    const result = await this.runStoreOperation("readonly", (store, resolve) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(Array.isArray(request.result) ? request.result : []);
      request.onerror = () => resolve([]);
    });
    return Array.isArray(result) ? result : [];
  }

  async migrateLegacyContentToIndexedDb() {
    if (this.migrationPromise != null) {
      return this.migrationPromise;
    }

    this.migrationPromise = (async () => {
      if (!this.isIndexedDbSupported()) {
        return;
      }

      const legacyContentList = this.getLegacyContentList();
      if (!Array.isArray(legacyContentList) || legacyContentList.length === 0) {
        return;
      }

      try {
        for (const legacyContent of legacyContentList) {
          if (legacyContent == null || typeof legacyContent.pageName === "undefined") {
            continue;
          }

          const cacheKey = this.buildCacheKey(legacyContent.pageName);
          await this.setIndexedDbValue({
            cacheKey,
            pageName: legacyContent.pageName,
            content: legacyContent.content
          });
        }
        this.removeLegacyContentKey();
      } catch (error) {
        // Leave the legacy cache in place if migration does not fully complete.
      }
    })();

    return this.migrationPromise;
  }

  async getContentFromStorage() {
    await this.migrateLegacyContentToIndexedDb();

    if (!this.isIndexedDbSupported()) {
      return this.getLegacyContentList();
    }

    try {
      const indexedDbItems = await this.getAllIndexedDbValues();
      if (indexedDbItems.length > 0) {
        return indexedDbItems
          .filter(item => item != null && typeof item.pageName !== "undefined")
          .map(item => ({ pageName: item.pageName, content: item.content }));
      }
    } catch (error) {
      // Fall back to legacy storage parsing below.
    }

    return this.getLegacyContentList();
  }

  async getCachedPageContent(pageName) {
    if (typeof pageName === "undefined") {
      return null;
    }

    await this.migrateLegacyContentToIndexedDb();
    const cacheKey = this.buildCacheKey(pageName);

    if (!this.isIndexedDbSupported()) {
      const contentList = this.getLegacyContentList();
      return contentList.find(contentObject => contentObject.pageName === pageName) || null;
    }

    try {
      const entry = await this.getIndexedDbValue(cacheKey);
      if (entry != null) {
        return { pageName: entry.pageName, content: entry.content };
      }
    } catch (error) {
      // Fall back to legacy storage below.
    }

    const contentList = this.getLegacyContentList();
    return contentList.find(contentObject => contentObject.pageName === pageName) || null;
  }

  async addContentToStorage(pageName, content) {
    if (typeof pageName === "undefined") {
      return false;
    }

    await this.migrateLegacyContentToIndexedDb();
    const entry = {
      cacheKey: this.buildCacheKey(pageName),
      pageName,
      content
    };

    if (this.isIndexedDbSupported()) {
      try {
        const saved = await this.setIndexedDbValue(entry);
        if (saved) {
          return true;
        }
      } catch (error) {
        // Fall back to localStorage below.
      }
    }

    const contentList = this.getLegacyContentList();
    const contentIndex = contentList.findIndex(contentObject => contentObject.pageName === pageName);
    if (contentIndex > -1) {
      contentList[contentIndex] = { pageName, content };
    } else {
      contentList.push({ pageName, content });
    }
    this.setLegacyContentList(contentList);
    return true;
  }

  async removeContentFromStorage(pageName) {
    if (typeof pageName === "undefined") {
      return false;
    }

    await this.migrateLegacyContentToIndexedDb();

    if (this.isIndexedDbSupported()) {
      try {
        await this.deleteIndexedDbValue(this.buildCacheKey(pageName));
      } catch (error) {
        // Also try legacy cleanup below.
      }
    }

    const contentList = this.getLegacyContentList();
    const updatedContentList = contentList.filter(contentObject => contentObject.pageName !== pageName);
    if (updatedContentList.length !== contentList.length) {
      this.setLegacyContentList(updatedContentList);
    }

    return true;
  }

  buildCachedResponse(content) {
    return { data: { data: content, statusCode: 200 } };
  }

  async getPageContentByPage(pageName, callback, chartApp) {
    if (typeof pageName === "undefined") {
      return;
    }

    try {
      const cachedContent = await this.getCachedPageContent(pageName);
      if (cachedContent != null) {
        callback(this.buildCachedResponse(cachedContent.content));
        return;
      }
    } catch (error) {
      // Safe fallback to API request below.
    }

    const params = new URLSearchParams();
    params.append("pageName", pageName);
    params.append("userId", 0);

    await axios
      .post(API_CONTENT_URL + "/get-content-by-page", params, { headers: await authHeader() })
      .then(async response => {
        await this.addContentToStorage(pageName, response.data.data);
        callback(response);
      })
      .catch(error => {
        error.message += ErrorService.mergeError(error.response);
        ErrorService.processError("ContentService.getPageContent", error, "admin", chartApp);
      });
  }

  async deleteContent(contentId, callback, chartApp) {
    const params = new URLSearchParams();
    params.append("contentId", contentId);
    params.append("userId", UserService.getUserId());
    await axios
      .post(API_CONTENT_URL + "/delete-content", params, { headers: await authHeader() })
      .then(response => callback(response))
      .catch(error => {
        error.message += ErrorService.mergeError(error.response);
        ErrorService.processError("ContentService.deleteContent", error, "admin", chartApp);
      });
  }

  async saveContent(formContentObject, callback, chartApp) {
    const params = new URLSearchParams();
    params.append("contentObject", JSON.stringify(formContentObject));
    params.append("userId", UserService.getUserId());
    await axios
      .post(API_CONTENT_URL + "/save-content", params, { headers: await authHeader() })
      .then(response => callback(response))
      .catch(error => {
        error.message += ErrorService.mergeError(error.response);
        ErrorService.processError("EditContent.saveContent", error, "admin", chartApp);
      });
  }

  async getPageContent(pageName, callback, chartApp) {
    try {
      const cachedContent = await this.getCachedPageContent(pageName);
      if (cachedContent != null) {
        callback(this.buildCachedResponse(cachedContent.content));
        return;
      }
    } catch (error) {
      // Safe fallback to API request below.
    }

    const userId = UserService.getUserId();
    if (typeof pageName === "undefined" || typeof userId === "undefined") {
      return;
    }

    const params = new URLSearchParams();
    params.append("pageName", pageName);
    params.append("userId", userId);
    await axios
      .post(API_CONTENT_URL + "/get-content-by-page", params, { headers: await authHeader() })
      .then(async response => {
        await this.addContentToStorage(pageName, response.data.data);
        callback(response);
      })
      .catch(error => {
        error.message += ErrorService.mergeError(error.response);
        ErrorService.processError("ContentService.getPageContent", error, "admin", chartApp);
      });
  }

  async getContentById(title, contentId, callback, chartApp) {
    const params = new URLSearchParams();
    params.append("contentId", contentId);
    params.append("userId", UserService.getUserId());
    await axios
      .post(API_CONTENT_URL + "/get-content-by-id", params, { headers: await authHeader() })
      .then(response => callback(response, title))
      .catch(error => {
        error.message += ErrorService.mergeError(error.response);
        ErrorService.processError("ContentService.getContentById", error, "admin", chartApp);
      });
  }

  getInitiativeDescriptionConfiguration() {
    return {
      direction: "ltr",
      height: 150,
      tabsize: 2,
      dialogsInBody: true,
      toolbar: [
//        ["style", ["fontname", "fontsize", "color", "bold", "italic", "underline", "strikethrough", "clear"]],
//        ["Paragraph style", ["style", "ol", "ul", "paragraph", "height"]],
//        ["Insert", ["picture"]],
//        ["Misc", ["fullscreen", "codeview", "undo", "redo", "help"]],
//        ["mybutton", ["hello"]]
      ],
      addDefaultFonts: false,
      fontNames: ["Arial", "Arial Black", "Comic Sans MS", "Courier New",
        "Franklin Gothic Book", "Franklin Gothic Demi", "Franklin Gothic Demi Cond", "Franklin Gothic Heavy", "Franklin Gothic Medium", "Franklin Gothic Medium Cond",
        "Helvetica Neue", "Helvetica", "Impact", "Lucida Grande",
        "Noto Sans", "Noto Sans Black", "Noto Sans ExtraBold", "Noto Sans ExtraLight", "Noto Sans Light", "Noto Sans Medium", "Noto Sans SemiBold", "Noto Sans Thin",
        "Segoe UI", "Segoe UI Black",
        "Tahoma", "Times New Roman", "Verdana"],
      fontNamesIgnoreCheck: ["Franklin Gothic Book", "Franklin Gothic Demi", "Franklin Gothic Demi Cond", "Franklin Gothic Heavy", "Franklin Gothic Medium", "Franklin Gothic Medium Cond",
        "Noto Sans", "Noto Sans Black", "Noto Sans ExtraBold", "Noto Sans ExtraLight", "Noto Sans Light", "Noto Sans Medium", "Noto Sans SemiBold", "Noto Sans Thin",
        "Segoe UI", "Segoe UI Black"],
    };
  }

  getContentConfiguration() {
    return {
      direction: "ltr",
      height: 350,
      tabsize: 2,
      dialogsInBody: true,
      toolbar: [
        ["style", ["fontname", "fontsize", "color", "bold", "italic", "underline", "strikethrough", "clear"]],
        ["Paragraph style", ["style", "ol", "ul", "paragraph", "height"]],
        ["Insert", ["picture", "link", "table", "hr"]],
        ["Misc", ["fullscreen", "codeview", "undo", "redo", "help"]],
        ["mybutton", ["hello"]]
      ],
      addDefaultFonts: false,
      fontNames: ["Arial", "Arial Black", "Comic Sans MS", "Courier New",
        "Franklin Gothic Book", "Franklin Gothic Demi", "Franklin Gothic Demi Cond", "Franklin Gothic Heavy", "Franklin Gothic Medium", "Franklin Gothic Medium Cond",
        "Helvetica Neue", "Helvetica", "Impact", "Lucida Grande",
        "Noto Sans", "Noto Sans Black", "Noto Sans ExtraBold", "Noto Sans ExtraLight", "Noto Sans Light", "Noto Sans Medium", "Noto Sans SemiBold", "Noto Sans Thin",
        "Segoe UI", "Segoe UI Black",
        "Tahoma", "Times New Roman", "Verdana"],
      fontNamesIgnoreCheck: ["Franklin Gothic Book", "Franklin Gothic Demi", "Franklin Gothic Demi Cond", "Franklin Gothic Heavy", "Franklin Gothic Medium", "Franklin Gothic Medium Cond",
        "Noto Sans", "Noto Sans Black", "Noto Sans ExtraBold", "Noto Sans ExtraLight", "Noto Sans Light", "Noto Sans Medium", "Noto Sans SemiBold", "Noto Sans Thin",
        "Segoe UI", "Segoe UI Black"],
    };
  }
}

export default new ContentService();
